<script lang="ts" setup>
import {ref, watch} from 'vue'
import {route} from '@/common/utils/ziggy'
import axios from 'axios'
import BulkUserImportCsvInput from '~components/admin/BulkUserImportCsvInput.vue'
import BulkUserImportOptions from '~components/admin/BulkUserImportOptions.vue'
import BulkUserImportPreview from '~components/admin/BulkUserImportPreview.vue'
import BulkUserImportActions from '~components/admin/BulkUserImportActions.vue'
import type {
  CreateUserRow,
  ParsedRow,
  ValidateResponse,
  ImportResponse,
} from '@/types/bulk-user-import'

// State
const csvText = ref('')
const defaultSchoolId = ref<number | null>(null)
const sendEmail = ref(true)
const rows = ref<ParsedRow[]>([])
const parsedRows = ref<CreateUserRow[]>([]) // stored for row removal
const importResult = ref<ImportResponse | null>(null)
const busy = ref(false)
const hasBeenValidated = ref(false)

const csvInput = ref<InstanceType<typeof BulkUserImportCsvInput> | null>(null)
const previewTable = ref<InstanceType<typeof BulkUserImportPreview> | null>(null)

// Derived
function hasErrors() {
  return rows.value.some(r => r.errors.length > 0)
}
function hasCsv() {
  return csvText.value.trim().length > 0
}

// Debounced validation
let validateTimer: ReturnType<typeof setTimeout> | null = null
function scheduleValidate() {
  if (validateTimer) clearTimeout(validateTimer)
  validateTimer = setTimeout(() => doValidate(), 500)
}

// Parse CSV into rows
function parseCsv(text: string): CreateUserRow[] {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim())
  if (lines.length < 2) return []

  const firstLine = lines[0].toLowerCase()
  const hasHeader = firstLine.startsWith('email') || firstLine.includes(',email,')
  const dataLines = hasHeader ? lines.slice(1) : lines

  return dataLines.map(line => {
    const parts = line.split(',').map(s => s.trim())
    return {
      email: parts[0] || '',
      firstName: parts[1] || '',
      lastName: parts[2] || '',
      type: (parts[3] || 'teacher') as CreateUserRow['type'],
      gradeLevel: parts[4] || null,
      schoolId: parts[5] ? Number(parts[5]) : null,
    }
  })
}

async function doValidate() {
  const parsed = parseCsv(csvText.value)
  parsedRows.value = parsed
  if (parsed.length === 0) {
    rows.value = []
    hasBeenValidated.value = false
    return
  }

  busy.value = true
  try {
    const response = await axios.post<ValidateResponse>(
      route('admin.bulk_users.validate'),
      {
        rows: parsed,
        options: {
          defaultSchoolId: defaultSchoolId.value,
          sendWelcomeEmail: sendEmail.value,
        },
      },
    )
    rows.value = response.data.rows
    hasBeenValidated.value = true
    importResult.value = null
  } catch (e: any) {
    console.error('Validation failed:', e)
    hasBeenValidated.value = false
    // If the server returned validation errors, surface them as a single-row error
    if (e?.response?.status === 422 && e?.response?.data?.errors) {
      const messages: string[] = []
      for (const field of Object.keys(e.response.data.errors)) {
        messages.push(...e.response.data.errors[field])
      }
      rows.value = [{
        index: 0, email: '', firstName: '', lastName: '',
        type: 'teacher' as any, gradeLevel: null, schoolId: null,
        errors: [{ field: 'general', message: messages.join('; ') }],
      }]
    }
  } finally {
    busy.value = false
  }
}

async function doImport() {
  const parsed = parseCsv(csvText.value)
  if (parsed.length === 0) return

  busy.value = true
  try {
    const response = await axios.post<ImportResponse>(
      route('admin.bulk_users.import'),
      {
        rows: parsed,
        options: {
          defaultSchoolId: defaultSchoolId.value,
          sendWelcomeEmail: sendEmail.value,
        },
      },
    )
    importResult.value = response.data
    rows.value = []
    hasBeenValidated.value = false
  } catch (e) {
    console.error('Import failed:', e)
  } finally {
    busy.value = false
  }
}

function handleClear() {
  csvText.value = ''
  rows.value = []
  parsedRows.value = []
  importResult.value = null
  hasBeenValidated.value = false
  csvInput.value?.clear()
}

function handleRemoveRow(index: number) {
  // Remove the row from the parsed list and regenerate CSV text
  parsedRows.value.splice(index, 1)
  rows.value = [] // clear preview while re-validating
  if (parsedRows.value.length === 0) {
    csvText.value = ''
    csvInput.value?.clear()
    return
  }
  rebuildCsvAndValidate()
}

function handleSaveRow(index: number, values: { email: string; fullName: string; type: string; gradeLevel: string; schoolId: string }) {
  const nameParts = values.fullName.trim().split(' ')
  parsedRows.value[index] = {
    email: values.email,
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
    type: values.type as any,
    gradeLevel: values.gradeLevel || null,
    schoolId: values.schoolId ? Number(values.schoolId) : null,
  }
  rebuildCsvAndValidate()
}

function rebuildCsvAndValidate() {
  const header = 'email,first_name,last_name,user_type,grade_level,school_id'
  const dataLines = parsedRows.value.map(r =>
    [r.email, r.firstName, r.lastName, r.type, r.gradeLevel || '', r.schoolId || ''].join(',')
  )
  suppressWatcher = true
  csvText.value = [header, ...dataLines].join('\n')
  suppressWatcher = false
  doValidate()
}

function handleAddRow() {
  // Insert blank row at top
  parsedRows.value.unshift({
    email: '', firstName: '', lastName: '',
    type: 'teacher' as any, gradeLevel: null, schoolId: null,
  })
  rebuildCsvAndValidate()
}

// Watch for CSV changes → schedule validation (skip if already doing it programmatically)
let suppressWatcher = false
watch(csvText, () => {
  if (suppressWatcher) return
  previewTable.value?.cancelEdit()
  importResult.value = null
  scheduleValidate()
})

// Watch for option changes → re-validate immediately
watch([defaultSchoolId, sendEmail], () => {
  if (hasCsv()) {
    importResult.value = null
    doValidate()
  }
})
</script>

<template>
  <div class="bulk-users-page">
    <h3>Bulk User Import</h3>
    <p class="text-muted small">Enter CSV data, upload a file, or add rows below</p>

    <BulkUserImportPreview
      ref="previewTable"
      :rows="rows"
      :busy="busy"
      :result="importResult"
      @remove="handleRemoveRow"
      @save="handleSaveRow"
      @add="handleAddRow"
    />

    <BulkUserImportOptions
      :school-id="defaultSchoolId"
      :send-email="sendEmail"
      @update:schoolId="defaultSchoolId = $event"
      @update:sendEmail="sendEmail = $event"
    />

    <BulkUserImportCsvInput
      ref="csvInput"
      :value="csvText"
      @csv-updated="csvText = $event"
    />

    <BulkUserImportActions
      :has-csv="hasCsv()"
      :has-errors="hasErrors()"
      :busy="busy"
      :show-import="hasBeenValidated && !hasErrors()"
      @clear="handleClear"
      @validate="doValidate"
      @import="doImport"
    />
  </div>
</template>

<style lang="scss" scoped>
.bulk-users-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem;
}
</style>
